/**
 * CardSearchPanel - Shared card search + filter + results shell
 *
 * Owns search, filters, keyboard navigation and the async states. Callers inject their own
 * target controls (e.g. a deck board selector) and row rendering.
 */
import clsx from 'clsx';
import type { KeyboardEvent, ReactElement, ReactNode } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { IoAlertCircle, IoClose } from 'react-icons/io5';

import Search from '@/components/Search/Search';
import Spinner from '@/components/Spinner/Spinner';
import useCardIndex from '@/hooks/useCardIndex';
import { useOwnedCardNames, useOwnedPrintingsByName } from '@/hooks/useCollectionAvailability';
import type { MinimalCard } from '@/services/cardDatabase';

import CardSearchFilters from './CardSearchFilters';
import {
  compareByOwnedThenName,
  EMPTY_CARD_SEARCH_FILTERS,
  isLegalInFormat,
  matchesCardSearchFilters,
  type CardSearchFilterState,
} from './cardSearchFiltering';

const MAX_SEARCH_RESULTS = 100;

export interface CardSearchRowRenderProps {
  card: MinimalCard;
  isHighlighted: boolean;
  onMouseEnter: () => void;
}

export interface CardSearchPanelProps {
  title?: string;
  onClose?: () => void;
  /** Rendered in the sticky header under the filters, e.g. a deck board selector. */
  targetControls?: ReactNode;
  /** Drops results illegal in this format; null or omitted skips the check. */
  legalityFormat?: string | null;
  /** Fired when Enter is pressed on the highlighted result. */
  onActivate?: (card: MinimalCard) => void;
  renderRow: (props: CardSearchRowRenderProps) => ReactNode;
  emptyHint?: string;
  className?: string;
}

export default function CardSearchPanel({
  title = 'Add Cards',
  onClose,
  targetControls,
  legalityFormat,
  onActivate,
  renderRow,
  emptyHint = 'Use arrow keys to navigate, Enter to add',
  className,
}: CardSearchPanelProps): ReactElement {
  // Only the debounced query lives here — a controlled input would re-render every result row
  // (each of which runs ownership hooks) on every keystroke.
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<CardSearchFilterState>(EMPTY_CARD_SEARCH_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const listRef = useRef<HTMLDivElement>(null);

  const { loading, error, searchCardsByPrefix, getCardById } = useCardIndex();
  const ownedCardNames = useOwnedCardNames();
  const ownedPrintings = useOwnedPrintingsByName();

  const results = useMemo(() => {
    const trimmed = query.trim();
    if (!trimmed) return [];

    // searchCardsByPrefix indexes each name variant separately, so split cards match twice.
    const seen = new Set<string>();
    const matches: MinimalCard[] = [];
    for (const card of searchCardsByPrefix(trimmed, MAX_SEARCH_RESULTS)) {
      if (seen.has(card.id)) continue;
      if (!matchesCardSearchFilters(card, filters)) continue;
      if (!isLegalInFormat(card, legalityFormat)) continue;
      seen.add(card.id);
      matches.push(card);
    }

    matches.sort((a, b) => compareByOwnedThenName(a, b, ownedCardNames));

    // Show the user's own printing where they have one, rather than an arbitrary one.
    return matches.map((card) => {
      const ownedId = ownedPrintings.get(card.name_lower);
      return (ownedId && getCardById(ownedId)) || card;
    });
  }, [query, searchCardsByPrefix, filters, legalityFormat, ownedCardNames, ownedPrintings, getCardById]);

  const updateHighlight = useCallback((index: number) => {
    setHighlightedIndex(index);
    const item = listRef.current?.querySelectorAll('[data-card-row]')[index];
    item?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, []);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        updateHighlight(Math.min(highlightedIndex + 1, results.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        updateHighlight(Math.max(highlightedIndex - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (results[highlightedIndex]) onActivate?.(results[highlightedIndex]);
        break;
    }
  };

  const hasQuery = query.trim() !== '';

  return (
    <div className={clsx('bg-obsidian border-storm flex h-full flex-col rounded-xl border', className)} onKeyDown={handleKeyDown}>
      <div className="border-storm sticky top-0 z-10 shrink-0 space-y-3 border-b p-4">
        {onClose && (
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-zinc-200">{title}</h3>
            <button
              type="button"
              onClick={onClose}
              className="hover:text-silver -mr-1 rounded p-1 text-zinc-400 transition-colors hover:bg-zinc-800"
              aria-label="Close card search panel"
            >
              <IoClose className="h-5 w-5" />
            </button>
          </div>
        )}

        <Search
          onSearch={(next) => {
            setQuery(next);
            setHighlightedIndex(0);
          }}
          placeholder="Search cards by name..."
          isLoading={loading}
          ariaLabel="Search cards"
        />

        <CardSearchFilters
          filters={filters}
          onChange={setFilters}
          isExpanded={showFilters}
          onToggleExpanded={() => setShowFilters((open) => !open)}
        />

        {targetControls}
      </div>

      <div ref={listRef} className="flex-1 overflow-y-auto">
        {loading && (
          <div className="flex flex-col items-center justify-center gap-3 py-12">
            <Spinner size="md" />
            <p className="text-ash text-sm">Loading card database...</p>
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center justify-center gap-3 px-4 py-12 text-center">
            <IoAlertCircle className="text-danger h-10 w-10" />
            <p className="text-danger text-sm">{error}</p>
          </div>
        )}

        {!loading && !error && !hasQuery && (
          <div className="text-ash flex flex-col items-center justify-center gap-2 py-12 text-center">
            <p className="text-sm">Type a card name to search</p>
            <p className="text-xs opacity-60">{emptyHint}</p>
          </div>
        )}

        {!loading && !error && hasQuery && results.length === 0 && (
          <div className="text-ash flex flex-col items-center justify-center gap-2 py-12 text-center">
            <p className="text-sm">No cards found for "{query}"</p>
            <p className="text-xs opacity-60">Try a different search term or clear the filters</p>
          </div>
        )}

        {!loading && !error && results.length > 0 && (
          <>
            <div className="divide-storm divide-y">
              {results.map((card, index) => (
                <div key={card.id} data-card-row role="option" aria-selected={index === highlightedIndex}>
                  {renderRow({ card, isHighlighted: index === highlightedIndex, onMouseEnter: () => updateHighlight(index) })}
                </div>
              ))}
            </div>
            <div className="text-ash border-storm border-t px-4 py-2 text-center text-xs">
              Showing {results.length} result{results.length !== 1 ? 's' : ''}
              {results.length >= MAX_SEARCH_RESULTS && ' (limit reached)'}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
